/**
 * Confetti moments. One lazily-created full-viewport canvas shared by every
 * burst (creating a canvas per burst leaks contexts). All entry points check
 * the celebrations setting; CSS-side animation uses the --task-dur-* tokens,
 * which the same setting zeroes via [data-celebrations="off"].
 */
import confetti from "canvas-confetti";
import type { CreateTypes } from "canvas-confetti";
import { useSettings } from "@/store/settings";

let instance: CreateTypes | null = null;

function fire(): CreateTypes | null {
  if (!useSettings.getState().celebrationsEnabled) return null;
  if (!instance) {
    const canvas = document.createElement("canvas");
    canvas.style.cssText = "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999";
    canvas.setAttribute("aria-hidden", "true");
    document.body.appendChild(canvas);
    instance = confetti.create(canvas, { resize: true, useWorker: true });
  }
  return instance;
}

/** Viewport-normalized origin of an element (confetti wants 0..1). */
function originOf(el: Element | null): { x: number; y: number } {
  if (!el) return { x: 0.5, y: 0.6 };
  const r = el.getBoundingClientRect();
  return {
    x: (r.left + r.width / 2) / window.innerWidth,
    y: (r.top + r.height / 2) / window.innerHeight,
  };
}

/** Small burst at the checkbox on a single completion. */
export function celebrateComplete(fromEl: Element | null): void {
  const c = fire();
  if (!c) return;
  void c({
    particleCount: 22,
    startVelocity: 18,
    spread: 55,
    gravity: 1.1,
    ticks: 90,
    scalar: 0.7,
    origin: originOf(fromEl),
    disableForReducedMotion: false,
  });
}

/** Side cannons when a list reaches zero open tasks. */
export function celebrateListZero(): void {
  const c = fire();
  if (!c) return;
  const shot = (x: number, angle: number) =>
    void c({
      particleCount: 70,
      angle,
      spread: 60,
      startVelocity: 45,
      gravity: 0.9,
      ticks: 200,
      origin: { x, y: 0.7 },
      disableForReducedMotion: false,
    });
  shot(0, 60);
  shot(1, 120);
}

/** Big center burst for achievements and streak milestones. */
export function celebrateBig(): void {
  const c = fire();
  if (!c) return;
  void c({
    particleCount: 140,
    spread: 100,
    startVelocity: 38,
    gravity: 0.85,
    ticks: 240,
    origin: { x: 0.5, y: 0.45 },
    disableForReducedMotion: false,
  });
}
